import type { FormikProps } from 'formik';
import { Card, CardHeader, Divider } from '@mui/material';

import {
	AddressInfo,
	AssessmentDates,
	CandidateInfo,
	ContactInfo,
	CreatedAt,
	EmploymentInfo,
	PrefAssessmentDates,
	StatusInfo,
	TermsAndConditions,
	TestingCentre,
	Tests,
} from '../../../components/request';
import type { CandidateProps, RequestProps, SupplierProps } from '../../../models';
import { useGetTestsQuery } from '../../../slices/apis/testApi';
import { useGetSupplierQuery } from '../../../slices/apis/testingCentreApi';
import { fIsValidDate } from '../../../utils/formatTime';
import RequestDetailSkeleton from '../RequestDetailSkeleton';

interface RequestReviewProps {
	formik: FormikProps<{
		supplierId?: string;
		tests: { id: string }[];
		prefAssessmentDates: (Date | null)[];
		candidate?: CandidateProps;
		tncAccepted: boolean;
		extraInfo?: string | null;
	}>;
}

interface SingleRequestDetailProps {
	request: RequestProps & { supplier?: Pick<SupplierProps, 'name' | 'address'> };
	formik?: FormikProps<{ tncAccepted: boolean }>;
	hideTestTotal?: boolean;
}

const RequestDetail: React.FC<SingleRequestDetailProps> = ({ request, formik, hideTestTotal = false }) => {
	const {
		id,
		candidate,
		tests,
		supplier,
		status,
		prefAssessmentDates,
		assessmentDates = [],
		createdAt,
		extraInfo,
	} = request;

	const renderCreatedAt = id && fIsValidDate(createdAt);
	const renderStatus = Boolean(status);
	const assessmentDate = assessmentDates[0];

	const hasCandidate = candidate?.firstname && candidate?.lastname;
	const hasContact = candidate?.email || candidate?.phone;
	const hasEmployment = candidate?.employer && candidate?.jobRole;
	const hasTestingCentre = supplier?.name && supplier?.address;

	return (
		<>
			{renderCreatedAt && <CreatedAt id={id} createdAt={createdAt} />}

			{renderStatus && <StatusInfo status={status} />}

			{hasCandidate && <CandidateInfo candidate={candidate} />}

			{hasContact && <ContactInfo candidate={candidate} />}

			<AddressInfo.SingleRequest candidate={candidate} />

			{hasEmployment && <EmploymentInfo candidate={candidate} />}

			{assessmentDate ? (
				<AssessmentDates assessmentDate={assessmentDate} />
			) : (
				<PrefAssessmentDates prefAssessmentDates={prefAssessmentDates} />
			)}

			{hasTestingCentre && <TestingCentre supplier={supplier} />}

			<CardHeader title="Tests" />
			<Divider sx={{ divider: { borderBottomWidth: 1.5 } }} />
			<Tests.SingleRequest tests={tests} hideTestTotal={hideTestTotal} extraInfo={extraInfo} />

			{formik && <TermsAndConditions formik={formik} />}
		</>
	);
};

export const RequestReview: React.FC<RequestReviewProps> = ({ formik }) => {
	const requestToReview = {} as RequestProps & { supplier?: Pick<SupplierProps, 'name' | 'address'> };

	const { data: supplier } = useGetSupplierQuery(formik.values.supplierId);
	const { data: allTests } = useGetTestsQuery();

	if (supplier) {
		requestToReview.supplier = supplier;
	}

	if (allTests) {
		requestToReview.tests = allTests.filter(
			(test) => formik.values.tests.findIndex((t) => t.id === test.id.toString()) > -1,
		);
	}

	if (formik.values.extraInfo) {
		requestToReview.extraInfo = formik.values.extraInfo;
	}

	requestToReview.prefAssessmentDates = formik.values.prefAssessmentDates.filter((date): date is Date => date !== null);

	if (formik.values.candidate) {
		requestToReview.candidate = { ...formik.values.candidate };
	}

	return requestToReview.supplier && requestToReview.tests ? (
		<Card>
			<RequestDetail request={requestToReview} formik={formik} hideTestTotal />
		</Card>
	) : (
		<RequestDetailSkeleton />
	);
};
